import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OperatorOrgPage, CreateOrgResponse, GrantCreditResponse, OperatorInstancesResponse } from '@lyra/shared';
import { apiFetch } from '@/lib/apiClient';
import { queryKeys } from '@/lib/queryKeys';
import { notifyError, notifySuccess } from '@/lib/useMutationToast';

/**
 * `/operator/organizatii` hooks — Plan 3f Task 3. Server side shipped in
 * Tasks 1+2 (server/src/routes/operator.ts): every endpoint is gated
 * server-side by `requirePlatformAdmin` (403 for anyone else) — the page
 * itself is additionally wrapped in the `operator` route Guard, so a
 * non-platform_admin never reaches these calls. Types are the shared
 * `@lyra/shared` ones (`OperatorOrgPage`/`CreateOrgResponse`/
 * `GrantCreditResponse`) since the server already exports them for this
 * exact contract.
 */

function useInvalidateOperatorOrgs() {
  const qc = useQueryClient();
  // Invalidating the shorter `['operator', 'organizations']` prefix (rather
  // than a specific params tuple) fuzzy-matches every page/search variant
  // cached under `queryKeys.operatorOrgs(params)` — mirrors nomenclatorHooks'
  // `useInvalidateNomenclator`.
  return () => qc.invalidateQueries({ queryKey: ['operator', 'organizations'] });
}

export interface OperatorOrgsParams {
  limit: number;
  offset: number;
  q: string;
}

/** `GET /operator/organizations?limit=&offset=&q=` — verified against
 *  `operator.ts`'s `app.get('/operator/organizations', ...)`: paginated,
 *  optionally search-filtered (name/slug, case-insensitive) list, each item
 *  enriched with member count / balance / tier / instance status. Returns
 *  `{ items, total }` (was a bare array — pagination fix). */
export function useOperatorOrgs(params: OperatorOrgsParams) {
  const qs = new URLSearchParams();
  qs.set('limit', String(params.limit));
  qs.set('offset', String(params.offset));
  if (params.q.trim()) qs.set('q', params.q.trim());

  return useQuery({
    queryKey: queryKeys.operatorOrgs(params),
    queryFn: () => apiFetch<OperatorOrgPage>(`/operator/organizations?${qs.toString()}`),
    placeholderData: (prev) => prev,
  });
}

/** `POST /operator/organizations {name,slug,adminEmail}` — verified against
 *  `operator.ts`: 201 `{organization,invitation:{id,email,link}}` on success
 *  (creates the org + a pending admin-role invitation, no self-signup);
 *  400 missing fields; 409 duplicate slug / duplicate email. Both 409
 *  variants carry their own distinct Romanian message from the server, so
 *  the page shows `ApiClientError.message` directly rather than special-
 *  casing the status like `/echipa`'s single-message 409 does. */
export function useCreateOrg() {
  const invalidate = useInvalidateOperatorOrgs();
  return useMutation({
    mutationFn: (values: { name: string; slug: string; adminEmail: string }) =>
      apiFetch<CreateOrgResponse>('/operator/organizations', {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Organizația a fost creată.');
    },
    onError: (error) => notifyError(error, 'Crearea organizației a eșuat.'),
  });
}

/** `POST /operator/organizations/:id/credit {amountEur}` — verified against
 *  `operator.ts`: 200 `{organizationId,balanceEur}` on success; 400 non-
 *  positive amount; 404 unknown org. Invalidating `operatorOrgs` refreshes
 *  the table's Sold column from the recomputed balance the server returns. */
export function useGrantCredit() {
  const invalidate = useInvalidateOperatorOrgs();
  return useMutation({
    mutationFn: ({ id, amountEur }: { id: string; amountEur: number }) =>
      apiFetch<GrantCreditResponse>(`/operator/organizations/${id}/credit`, {
        method: 'POST',
        body: JSON.stringify({ amountEur }),
      }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Creditele au fost acordate.');
    },
    onError: (error) => notifyError(error, 'Acordarea creditelor a eșuat.'),
  });
}

/** `GET /operator/instances` (Task 8) — cross-org list of active Fleet
 *  rentals (`operator.ts`'s `listActiveFleetInstances`): still-open sessions
 *  plus any stuck `destroy_failed` one. Polled every 30s while mounted — a
 *  much shorter, purpose-specific console page (unlike `useOperatorOrgs`,
 *  no pagination/search params to key off). */
export function useOperatorInstances() {
  return useQuery({
    queryKey: queryKeys.operatorInstances,
    queryFn: () => apiFetch<OperatorInstancesResponse>('/operator/instances'),
    refetchInterval: 30_000,
  });
}
